
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';
import type { Article } from '../../types';
import {
  formatPDFDate,
  sanitizePDFContent,
  PDF_COLORS,
  PDF_FONTS,
} from '../../utils/pdf';

// Register standard fonts (using built-in Helvetica)
Font.register({
  family: 'Helvetica',
  fonts: [
    { src: 'Helvetica' },
    { src: 'Helvetica-Bold', fontWeight: 'bold' },
  ],
});

export interface ArticlePDFProps {
  article: Article;
  siteName?: string;
  siteUrl?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: PDF_COLORS.background,
    padding: 60,
    fontFamily: 'Helvetica',
  },
  header: {
    backgroundColor: PDF_COLORS.primary,
    margin: -60,
    marginBottom: 40,
    padding: 30,
    paddingTop: 40,
    paddingBottom: 25,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  logoIcon: {
    width: 40,
    height: 40,
    backgroundColor: 'transparent',
    borderRadius: 20,
    border: '2px solid rgba(255,255,255,0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoText: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: 'bold',
  },
  siteName: {
    fontSize: 18,
    color: '#ffffff',
    fontWeight: 'bold',
    letterSpacing: 0.5,
  },
  siteTagline: {
    fontSize: 9,
    color: 'rgba(255,255,255,0.7)',
    marginTop: 2,
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  category: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.8)',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  content: {
    flex: 1,
  },
  title: {
    fontSize: PDF_FONTS.title,
    fontWeight: 'bold',
    color: PDF_COLORS.text,
    marginBottom: 16,
    lineHeight: 1.3,
  },
  metaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
    paddingBottom: 16,
    borderBottom: `1px solid ${PDF_COLORS.border}`,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaLabel: {
    fontSize: PDF_FONTS.small,
    color: PDF_COLORS.textMuted,
  },
  metaValue: {
    fontSize: PDF_FONTS.small,
    color: PDF_COLORS.textLight,
    fontWeight: 'bold',
  },
  excerpt: {
    fontSize: PDF_FONTS.subtitle,
    color: PDF_COLORS.textLight,
    fontStyle: 'italic',
    marginBottom: 24,
    padding: 16,
    backgroundColor: '#f8fafc',
    borderLeft: `3px solid ${PDF_COLORS.accent}`,
    lineHeight: 1.5,
  },
  bodyText: {
    fontSize: PDF_FONTS.body,
    color: PDF_COLORS.text,
    lineHeight: 1.6,
    textAlign: 'justify',
  },
  paragraph: {
    marginBottom: 12,
  },
  footer: {
    marginTop: 40,
    paddingTop: 20,
    borderTop: `1px solid ${PDF_COLORS.border}`,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {
    flexDirection: 'column',
  },
  footerText: {
    fontSize: PDF_FONTS.caption,
    color: PDF_COLORS.textMuted,
  },
  footerLink: {
    fontSize: PDF_FONTS.caption,
    color: PDF_COLORS.accent,
  },
  pageNumber: {
    fontSize: PDF_FONTS.caption,
    color: PDF_COLORS.textMuted,
  },
  watermark: {
    position: 'absolute',
    bottom: 80,
    right: 60,
    opacity: 0.03,
    fontSize: 60,
    color: PDF_COLORS.primary,
    transform: 'rotate(-30deg)',
  },
});



/**
 * ArticlePDF Component
 * Renders a professional PDF document for articles with observatory branding
 */
export function ArticlePDF({
  article,
  siteName = 'Sternwarte Mannheim',
  siteUrl = 'sternwarte-mannheim.de',
}: ArticlePDFProps) {
  const sanitizedContent = sanitizePDFContent(article.content);
  const paragraphs = sanitizedContent.split('\n').filter((p) => p.trim());

  // Get category display name
  const categoryNames: Record<string, string> = {
    news: 'Aktuelles',
    astronomy: 'Astronomie',
    history: 'Geschichte',
  };
  const categoryDisplay = categoryNames[article.category] || article.category;

  return (
    <Document
      title={article.title}
      author={siteName}
      subject={article.excerpt || article.title}
      keywords="Sternwarte, Mannheim, Astronomie, Geschichte"
      creator={siteName}
      producer="Sternwarte Mannheim PDF Export"
    >
      <Page size="A4" style={styles.page}>
        {/* Header with Observatory Branding */}
        <View style={styles.header} fixed>
          <View style={styles.headerContent}>
            <View style={styles.logoSection}>
              <View style={styles.logoIcon}>
                <Text style={styles.logoText}>★</Text>
              </View>
              <View>
                <Text style={styles.siteName}>{siteName}</Text>
                <Text style={styles.siteTagline}>Alte Sternwarte Mannheim</Text>
              </View>
            </View>
            <View style={styles.headerRight}>
              <Text style={styles.category}>{categoryDisplay}</Text>
            </View>
          </View>
        </View>

        {/* Main Content */}
        <View style={styles.content}>
          {/* Title */}
          <Text style={styles.title}>{article.title}</Text>

          {/* Meta Information */}
          <View style={styles.metaContainer}>
            {article.publishedAt && (
              <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Veröffentlicht:</Text>
                <Text style={styles.metaValue}>
                  {formatPDFDate(article.publishedAt)}
                </Text>
              </View>
            )}
            {article.authorId && (
              <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Autor:</Text>
                <Text style={styles.metaValue}>{article.authorId}</Text>
              </View>
            )}
          </View>

          {/* Excerpt if available */}
          {article.excerpt && (
            <Text style={styles.excerpt}>{article.excerpt}</Text>
          )}

          {/* Article Content */}
          <View style={styles.bodyText}>
            {paragraphs.length > 0 ? (
              paragraphs.map((paragraph, index) => (
                <Text key={index} style={styles.paragraph}>
                  {paragraph.trim()}
                </Text>
              ))
            ) : (
              <Text style={styles.paragraph}>{sanitizedContent}</Text>
            )}
          </View>
        </View>

        {/* Watermark */}
        <Text style={styles.watermark}>★</Text>

        {/* Footer with Page Numbers */}
        <View style={styles.footer} fixed>
          <View style={styles.footerLeft}>
            <Text style={styles.footerText}>
              Generiert am {formatPDFDate(new Date().toISOString())}
            </Text>
            <Text style={styles.footerLink}>www.{siteUrl}</Text>
          </View>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
              `Seite ${pageNumber} von ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}

export default ArticlePDF;
